import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon, SlidersHorizontalIcon } from 'lucide-react';
import { useInventoryContext } from '../contexts/InventoryContext';
import { CatalogFilterState } from '../types/car';
import { formatARS } from '../utils/format';
import { FilterSelect } from './FilterSelect';
interface CatalogFiltersProps {
  filters: CatalogFilterState;
  onChange: (key: keyof CatalogFilterState, value: string) => void;
  onClear: () => void;
}
const priceSteps = [30000000, 40000000, 50000000, 60000000];
function unique(values: string[]): {
  value: string;
  label: string;
}[] {
  return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b)).map(v => ({
    value: v,
    label: v
  }));
}
export function CatalogFilters({
  filters,
  onChange,
  onClear
}: CatalogFiltersProps) {
  const {
    cars
  } = useInventoryContext();
  const [showMore, setShowMore] = useState(false);
  const activeCount = Object.values(filters).filter(Boolean).length;
  const options = useMemo(() => {
    const years = Array.from(new Set(cars.map(c => c.year))).sort((a, b) => b - a).map(y => ({
      value: String(y),
      label: String(y)
    }));
    const byBrand = filters.brand ? cars.filter(c => c.brand === filters.brand) : cars;
    const byModel = filters.model ? byBrand.filter(c => c.model === filters.model) : byBrand;
    return {
      brands: unique(cars.map(c => c.brand)),
      models: unique(byBrand.map(c => c.model)),
      versions: unique(byModel.map(c => c.version)),
      years,
      fuels: unique(cars.map(c => c.fuel)),
      transmissions: unique(cars.map(c => c.transmission)),
      conditions: unique(cars.map(c => c.condition)),
      prices: priceSteps.map(p => ({
        value: String(p),
        label: `Hasta ${formatARS(p)}`
      }))
    };
  }, [cars, filters.brand, filters.model]);
  return <section aria-label="Filtros" className="rounded-xl border border-line bg-panel p-4 sm:p-5" data-id="element-88" data-mp="1u4byqsqtpsjk">
      <div className="mb-4 flex items-center justify-between gap-3" data-id="element-89" data-mp="2xfj5gv0gatse">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-white" data-id="element-90" data-mp="2sgy1cki2vpyo">
          <SlidersHorizontalIcon className="h-4 w-4 text-neon" aria-hidden="true" data-id="element-91" data-mp="2sj1321slf7j8" />
          Filtrar vehículos
          {activeCount > 0 && <span className="rounded-full bg-neon px-2 py-0.5 text-xs font-bold text-ink" data-id="element-92" data-mp="31hd0onrvn5l6">{activeCount}</span>}
        </h2>
        <div className="flex items-center gap-4" data-id="element-93" data-mp="3gdc7lv392zoq">
          {activeCount > 0 && <button type="button" onClick={onClear} className="text-sm text-muted transition-colors duration-150 hover:text-white" data-id="element-94" data-mp="3bf0w7fxb4h4e">
              Limpiar
            </button>}
          <button type="button" onClick={() => setShowMore(v => !v)} aria-expanded={showMore} aria-controls="more-filters" className="flex items-center gap-1 whitespace-nowrap text-sm font-medium text-neon transition-colors duration-150 hover:text-neon-bright" data-id="element-95" data-mp="3l5dhjacy4ebl">
            {showMore ? 'Menos filtros' : 'Más filtros'}
            <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ${showMore ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-96" data-mp="mm28son25feg" />
          </button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-id="element-97" data-mp="cmbcchexsqbq">
        <FilterSelect id="f-brand" label="Marca" value={filters.brand} onChange={v => onChange('brand', v)} placeholder="Todas" options={options.brands} data-id="element-98" data-mp="3v12g7vuyjpa7" />
        <FilterSelect id="f-model" label="Modelo" value={filters.model} onChange={v => onChange('model', v)} placeholder="Todos" options={options.models} data-id="element-99" data-mp="3o99dwbn7nlt" />
        <FilterSelect id="f-version" label="Versión" value={filters.version} onChange={v => onChange('version', v)} placeholder="Todas" options={options.versions} data-id="element-100" data-mp="3q1pr14gm664g" />
        <FilterSelect id="f-year-from" label="Año desde" value={filters.yearFrom} onChange={v => onChange('yearFrom', v)} placeholder="Cualquiera" options={options.years} data-id="element-101" data-mp="3g4bnu6jpbdu3" />
        <FilterSelect id="f-year-to" label="Año hasta" value={filters.yearTo} onChange={v => onChange('yearTo', v)} placeholder="Cualquiera" options={options.years} data-id="element-102" data-mp="3937u9yhykkov" />
      </div>

      <AnimatePresence initial={false} data-id="element-103" data-mp="2z9r2t15anhod">
        {showMore && <motion.div id="more-filters" initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} className="overflow-hidden" data-id="element-104" data-mp="2ud0lyblo0i1w">
            <div className="grid gap-3 pt-3 sm:grid-cols-2 lg:grid-cols-4" data-id="element-105" data-mp="2gd90jpstjzdv">
              <FilterSelect id="f-price" label="Precio contado" value={filters.maxPrice} onChange={v => onChange('maxPrice', v)} placeholder="Sin límite" options={options.prices} data-id="element-106" data-mp="2be66f0edndko" />
              <FilterSelect id="f-fuel" label="Combustible" value={filters.fuel} onChange={v => onChange('fuel', v)} placeholder="Todos" options={options.fuels} data-id="element-107" data-mp="9m9lj06bkrd8" />
              <FilterSelect id="f-transmission" label="Transmisión" value={filters.transmission} onChange={v => onChange('transmission', v)} placeholder="Todas" options={options.transmissions} data-id="element-108" data-mp="i0xsrwa362pj" />
              <FilterSelect id="f-condition" label="Condición" value={filters.condition} onChange={v => onChange('condition', v)} placeholder="0 km y usados" options={options.conditions} data-id="element-109" data-mp="qw3ykv9cwks2" />
            </div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}